import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { US_STATES } from '@/src/homepage/shared/constants/usStates';
import styles from './StatesContainer.module.scss';

interface StatesContainerProps {
    languageSubdirectory?: string;
}

const buildStateHref = (slug: string, languageSubdirectory?: string) =>
    `${languageSubdirectory ? '/' + languageSubdirectory : ''}/d/united-states--${slug}/events/`;

// Matches the diagonal-arrow affordance the shared Tags pill component uses.
const ArrowIcon = () => (
    <svg
        className={styles.arrow}
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
        focusable="false"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12.2907 3.61849V3.61719H6.2168V4.93584H10.0845L3.57227 11.3512L4.51877 12.2836L11.1224 5.7782V9.74552H12.461V3.61849H12.2907Z"
            fill="currentColor"
        />
    </svg>
);

/**
 * Logged-out .com homepage module linking to every US state (+ DC) `/d/` browse
 * hub. Each state is a real server-rendered `<a href>` (anchor text = state
 * name) so crawlers can reach the state hubs from `/`. Laid out as two
 * independent, horizontally-scrollable rows of pills (scrollbar hidden, masked
 * edge fade). Each row packs its own pills at a fixed gap — the rows are not
 * column-aligned — so a wide label in one row (e.g. "District of Columbia")
 * never stretches the pill below it. States alternate rows (top = even index,
 * bottom = odd). Rendered only for the US (.com) behind the
 * `enable_homepage_geo_seo_links` flag; gating is handled by the caller.
 */
export const StatesContainer: React.FunctionComponent<StatesContainerProps> = ({
    languageSubdirectory,
}) => {
    const intl = useIntl();

    const rows = useMemo(() => {
        const states = US_STATES.map(({ slug, displayName }) => ({
            displayName,
            href: buildStateHref(slug, languageSubdirectory),
        }));
        // Alternate states across two rows (top = even index, bottom = odd).
        const topRow = states.filter((_, index) => index % 2 === 0);
        const bottomRow = states.filter((_, index) => index % 2 === 1);
        return [topRow, bottomRow];
    }, [languageSubdirectory]);

    return (
        <div className={styles.root} data-testid="states-container">
            <h2 className={styles.header}>
                {intl.formatMessage({
                    id: 'Explore by State',
                    defaultMessage: 'Explore by State',
                })}
            </h2>
            <div className={styles.scroller} data-testid="states-scroller">
                {rows.map((row, rowIndex) => (
                    <ul key={rowIndex} className={styles.row}>
                        {row.map(({ displayName, href }) => (
                            <li key={href}>
                                <a className={styles.pill} href={href}>
                                    {displayName}
                                    <ArrowIcon />
                                </a>
                            </li>
                        ))}
                    </ul>
                ))}
            </div>
        </div>
    );
};
